import { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, RefreshCw, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500',
  confirmed: 'bg-blue-500',
  preparing: 'bg-purple-500',
  out_for_delivery: 'bg-orange-500',
  delivered: 'bg-green-500',
  cancelled: 'bg-red-500'
};

const AdminPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const isAuthenticated = sessionStorage.getItem('admin_authenticated');
    if (!isAuthenticated) {
      navigate('/admin-login');
      return;
    }
    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load orders');
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}`, { status: newStatus });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update status');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_authenticated');
    toast.success('Logged out successfully');
    navigate('/admin-login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <header className="bg-gray-900/80 backdrop-blur-md border-b border-amber-500/30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/')} variant="ghost" className="text-amber-400 hover:bg-amber-500/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-amber-400 font-['Playfair_Display']">Admin Dashboard</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchOrders} variant="outline" className="border-amber-500/30 text-amber-400">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : orders.length === 0 ? (
          <Card className="p-12 text-center bg-gray-800 border-amber-500/30">
            <h2 className="text-2xl font-bold text-amber-400 mb-2">No orders yet</h2>
            <p className="text-gray-300">Orders will appear here</p>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-amber-400">All Orders ({orders.length})</h2>
            {orders.map(order => (
              <Card key={order.id} className="p-6 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-1 space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Order ID</p>
                        <p className="font-bold text-amber-400">{order.id.substring(0, 8).toUpperCase()}</p>
                        {order.petpooja_order_id && (
                          <p className="text-xs text-green-400">PetPooja: {order.petpooja_order_id}</p>
                        )}
                      </div>
                      <Badge className={`${statusColors[order.status]} text-white`}>
                        {order.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-400">Customer</p>
                        <p className="text-amber-100">{order.customer_name}</p>
                        <p className="text-sm text-gray-300">{order.customer_phone}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Type</p>
                        <p className="text-amber-100 uppercase font-semibold">{order.order_type}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-400 mb-2">Items</p>
                      <div className="space-y-1">
                        {order.items.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm text-amber-100">
                            <span>{item.name} x {item.quantity}</span>
                            <span>₹{item.price * item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-2 border-t-2 border-amber-500/30">
                      <div className="flex justify-between font-bold text-lg text-amber-400">
                        <span>Total</span>
                        <span>₹{order.total_amount}</span>
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-64">
                    <Select value={order.status} onValueChange={(value) => updateOrderStatus(order.id, value)}>
                      <SelectTrigger className="w-full bg-gray-700 border-amber-500/30 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="preparing">Preparing</SelectItem>
                        <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(order.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;